import { Measure, TimeseriesWindow } from "~/modules/measures/types";
import { TimeseriesStateManager } from "~/modules/visualization/components/Timeseries/TimeseriesStateManager";
import { TimeSeriesFetcher } from "~/modules/visualization/components/Timeseries/TimeSeriesFetcher";
import { TimeseriesCard } from "~/modules/visualization/components/Timeseries/TimeseriesCard";
import { TimeSeriesChart } from "~/modules/visualization/components/TimeSeriesChartV3";
import {
    VisualizationMode,
    VisualizationModeTypes,
} from "~/modules/visualization/components/Timeseries/types";
import {
    ASSET_TIMESERIES_V4_API_TOOLS_CONTROLS,
    TOKEN_TIMESERIES_V4_API_TOOLS_CONTROLS,
} from "~/modules/visualization/components/Timeseries/consts";
import { WindowResizeObserver } from "~/components/WindowResizeObserver";
import {
    AggregateFunctions,
    AggregateQueryInterval,
    ApiQuery,
    ApiQueryEndpoints,
    FilterOperators,
    SortDirections,
    Filter,
} from "~/modules/filters/types";
import {
    AssetTimeseriesColumns,
    AssetTimeseriesGroupBy,
} from "~/modules/asset_timeseries_v4/types";
import {
    ChartTypes,
    TimeSeriesItem,
    TokenTimeseriesColumns,
} from "~/modules/token_timeseries/types";
import {
    SIX_MONTHS_AGO,
    THREE_QUARTERS_AGO,
    TWO_WEEKS_AGO,
    THREE_YEARS_AGO,
} from "~/utils/dates/consts";
import { ExportTableNames } from "~/modules/downloads/types";
import { ColumnSpanConfig } from "~/types";

// // // //

const CHART_HEIGHT = 350;

const FULL_WIDTH_COL_SPANS: ColumnSpanConfig = {
    sm: 12,
    md: 12,
    lg: 12,
    xl: 12,
    xxl: 12,
};

/**
 * Builds an ApiQuery for a single measure's timeseries, aggregating all
 * matching rows into one series grouped by measure.
 */
function getMeasureTimeseriesQuery(props: {
    measureSlug: string;
    measureSlugField: string;
    dateField: string;
    groupBy: string;
    filters: Filter[];
}): ApiQuery {
    const { measureSlug, measureSlugField, dateField, groupBy, filters } =
        props;

    return {
        aggregate: {
            aggregateFunction: AggregateFunctions.Sum,
            groupBy,
            interval: AggregateQueryInterval.day,
            groupLimit: 5,
        },
        filter: {
            operator: FilterOperators.And,
            filters: [
                {
                    field: measureSlugField,
                    operator: FilterOperators.Equals,
                    value: measureSlug,
                },
                ...filters,
            ],
        },
        sort: {
            field: dateField,
            direction: SortDirections.asc,
        },
        pagination: {
            page: 1,
            perPage: 200,
        },
    };
}

/**
 * CatalogMetricTimeseriesChart
 * Renders the appropriate timeseries chart for a catalog measure based on
 * whether it has token-level and/or asset-level snapshots.
 */
export function CatalogMetricTimeseriesChart(props: { metric: Measure }) {
    const { metric } = props;

    const showTokenTimeseries = metric.has_token_snapshots;
    const showAssetTimeseries = metric.has_asset_snapshots;

    if (showTokenTimeseries && showAssetTimeseries) {
        return <div>Both Token and Asset Timeseries</div>;
    }

    if (showTokenTimeseries) {
        return <TokenTimeseriesChart metric={metric} />;
    }

    if (showAssetTimeseries) {
        return <AssetTimeseriesChart metric={metric} />;
    }

    return null;
}

// // // //

/**
 * MeasureTimeseriesCard
 * Fetches and renders a measure timeseries via TimeseriesCard, exposing the
 * shared data-download and API-tools controls.
 */
function MeasureTimeseriesCard(props: {
    title: string;
    mode: VisualizationMode;
    timeseriesWindow: TimeseriesWindow;
}) {
    const { title, mode, timeseriesWindow } = props;

    return (
        <div className="mb-6">
            <TimeseriesStateManager modes={[mode]}>
                {({
                    controls,
                    mode: modeControls,
                    aggregateQuery,
                    appliedQuery,
                    dataExportControls,
                }) => (
                    <TimeSeriesFetcher
                        query={appliedQuery}
                        endpoint={modeControls.activeMode.endpoint}
                    >
                        {({ data, loading }) => {
                            const series = data.map((item: TimeSeriesItem) => ({
                                item,
                                enabled: true,
                                chartType: ChartTypes.area,
                            }));

                            const chartDescription =
                                timeseriesWindow === "yearly"
                                    ? "Showing the top 5 assets over the last 3 years."
                                    : timeseriesWindow === "quarterly"
                                      ? "Showing the top 5 assets over the last 3 quarters."
                                      : timeseriesWindow === "monthly"
                                        ? "Showing the top 5 assets over the last 6 months."
                                        : "Showing the top 5 assets over the last 2 weeks.";

                            return (
                                <TimeseriesCard
                                    title={title}
                                    subtitle={chartDescription}
                                    height={CHART_HEIGHT}
                                    apiQuery={appliedQuery}
                                    loading={loading || modeControls.loading}
                                    empty={data.length === 0}
                                    modeControls={modeControls}
                                    controls={controls}
                                    exportData={dataExportControls}
                                    colSpans={FULL_WIDTH_COL_SPANS}
                                    aggregateQueryControls={{
                                        groupBy: aggregateQuery.groupBy,
                                        interval: aggregateQuery.interval,
                                        aggregateFunction:
                                            aggregateQuery.aggregateFunction,
                                    }}
                                    apiTools={{
                                        enabled:
                                            modeControls.activeMode.apiTools
                                                ?.enabled || false,
                                        baseUrl:
                                            modeControls.activeMode.apiTools
                                                ?.baseUrl || "",
                                    }}
                                >
                                    <WindowResizeObserver
                                        delay={1200}
                                        defaultPlaceholderHeight={CHART_HEIGHT}
                                    >
                                        <TimeSeriesChart
                                            series={series}
                                            area
                                            height={CHART_HEIGHT}
                                            stacked={controls.stacked.active}
                                            navigator={controls.navigator}
                                            yAxis={{ min: 0, enabled: true }}
                                            legend={{
                                                enabled: controls.legend.active,
                                            }}
                                        />
                                    </WindowResizeObserver>
                                </TimeseriesCard>
                            );
                        }}
                    </TimeSeriesFetcher>
                )}
            </TimeseriesStateManager>
        </div>
    );
}

// // // //

/**
 * TokenTimeseriesChart
 * Renders a token-level timeseries chart for the given measure.
 */
function TokenTimeseriesChart(props: { metric: Measure }) {
    const { metric } = props;

    const timeseriesWindow = metric.timeseries_window;

    const onOrAfterDate =
        timeseriesWindow === "yearly"
            ? THREE_YEARS_AGO
            : timeseriesWindow === "monthly"
              ? SIX_MONTHS_AGO
              : timeseriesWindow === "quarterly"
                ? THREE_QUARTERS_AGO
                : TWO_WEEKS_AGO;

    const mode: VisualizationMode = {
        key: metric.slug,
        type: VisualizationModeTypes.area,
        content: { label: metric.display_name },
        endpoint: ApiQueryEndpoints.TokenTimeseriesV4,
        query: getMeasureTimeseriesQuery({
            measureSlug: metric.slug,
            measureSlugField: TokenTimeseriesColumns.measureSlug,
            dateField: TokenTimeseriesColumns.date,
            groupBy: AssetTimeseriesGroupBy.asset,
            filters: [
                {
                    field: TokenTimeseriesColumns.date,
                    operator: FilterOperators.OnOrAfter,
                    value: onOrAfterDate.toISOString(),
                },
            ],
        }),
        queryControls: {
            groupBy: {
                enabled: false,
                value: AssetTimeseriesGroupBy.asset,
                allowedOptions: [AssetTimeseriesGroupBy.asset],
            },
        },
        visualizationControls: {
            stacked: {
                enabled: false,
                active: false,
            },
            legend: {
                enabled: true,
                active: false,
            },
            navigator: {
                enabled: false,
                active: false,
            },
        },
        dataExportControls: {
            enabled: true,
            exportTableName: ExportTableNames.token_timeseries_v4,
        },
        apiTools: TOKEN_TIMESERIES_V4_API_TOOLS_CONTROLS,
    };

    return (
        <MeasureTimeseriesCard
            title={metric.display_name}
            mode={mode}
            timeseriesWindow={timeseriesWindow}
        />
    );
}

// // // //

/**
 * AssetTimeseriesChart
 * Renders an asset-level timeseries chart for the given measure.
 */
function AssetTimeseriesChart(props: { metric: Measure }) {
    const { metric } = props;

    const timeseriesWindow = metric.timeseries_window;

    const mode: VisualizationMode = {
        key: metric.slug,
        type: VisualizationModeTypes.area,
        content: { label: metric.display_name },
        endpoint: ApiQueryEndpoints.AssetTimeseriesV4,
        query: getMeasureTimeseriesQuery({
            measureSlug: metric.slug,
            measureSlugField: AssetTimeseriesColumns.measureSlug,
            dateField: AssetTimeseriesColumns.date,
            groupBy: AssetTimeseriesGroupBy.asset,
            filters: [
                {
                    field: AssetTimeseriesColumns.tokenCount,
                    operator: FilterOperators.GreaterThan,
                    value: 0,
                },
                {
                    field: AssetTimeseriesColumns.date,
                    operator: FilterOperators.OnOrAfter,
                    value: TWO_WEEKS_AGO.toISOString(),
                },
            ],
        }),
        queryControls: {
            groupBy: {
                enabled: false,
                value: AssetTimeseriesGroupBy.asset,
                allowedOptions: [AssetTimeseriesGroupBy.asset],
            },
        },
        dataExportControls: {
            enabled: true,
            exportTableName: ExportTableNames.asset_timeseries_v4,
        },
        apiTools: ASSET_TIMESERIES_V4_API_TOOLS_CONTROLS,
    };

    return (
        <MeasureTimeseriesCard
            title={metric.display_name}
            mode={mode}
            timeseriesWindow={timeseriesWindow}
        />
    );
}
